"use client";
import { useLanguageContext } from "@/context/LanguageContext";
import { Dropdown } from "antd";
import classNames from "classnames";
import { useRouter } from "next/navigation";
import type { FC, ReactNode } from "react";
import { memo, useCallback } from "react";

export interface HeaderMenuItem {
  key: string;
  label: ReactNode;
  children?: {
    key: string;
    label: ReactNode;
    description?: string;
    icon?: ReactNode;
    href?: string;
  }[];
}

interface DropdownMenuProps {
  menuItem: HeaderMenuItem;
  currentPath: string;
}

const DropdownMenu: FC<DropdownMenuProps> = ({ menuItem, currentPath }) => {
  const router = useRouter();
  const { language } = useLanguageContext();

  const handleMenuClick = useCallback(
    (path: string) => {
      if (!path) {
        console.error("Path is undefined or null");
        return;
      }

      const pathStr = String(path);
      const isExternalUrl = /^https?:\/\//i.test(pathStr);

      if (isExternalUrl) {
        window.open(pathStr, "_blank");
      } else {
        router.push(`/${language}${pathStr}`);
      }
    },
    [language, router],
  );

  // If no children, render as a regular link
  if (!menuItem.children?.length) {
    return (
      <div
        className={classNames("cursor-pointer font-semibold text-base border-none hover:text-primary", {
          "text-primary": currentPath === menuItem.key,
        })}
        onClick={() => handleMenuClick(menuItem.key)}
      >
        {menuItem.label}
      </div>
    );
  }

  // With children, render as a dropdown
  const dropdownItems = menuItem.children.map(child => ({
    key: child.href || child.key,
    label: (
      <div className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded-lg">
        {child.icon && (
          <div className="mt-1 shadow size-8 flex items-center justify-center rounded-full">{child.icon}</div>
        )}
        <div>
          <div className="font-medium">{child.label}</div>
          {child.description && <div className="text-sm text-gray-500">{child.description}</div>}
        </div>
      </div>
    ),
  }));

  return (
    <Dropdown
      menu={{
        items: dropdownItems,
        onClick: ({ key }) => handleMenuClick(key),
      }}
      trigger={["click"]}
      overlayClassName="mt-1"
    >
      <div
        className={classNames(
          "cursor-pointer font-semibold text-base border-none hover:text-primary flex items-center",
          {
            "text-primary": currentPath.startsWith(menuItem.key),
          },
        )}
      >
        {menuItem.label}
      </div>
    </Dropdown>
  );
};

DropdownMenu.displayName = "DropdownMenu";

export default memo(DropdownMenu);
